import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Camera, ChevronRight, MapPin, MessageSquarePlus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, partnerCoverUrl, errorText, formatDay, formatDateTime } from "@/lib/partnerApi";
import { P_CARD, P_H1, P_H2, P_FIELD, P_BTN_COPPER, P_BTN_GHOST, BackLink, StatusPill, UrgentCard, PageLoading, ObraCover } from "@/components/partner/PartnerUI";

export default function PartnerObra() {
    const { leadId } = useParams();
    const [obra, setObra] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(() => {
        partnerApi.get(`/obras/${leadId}`).then(({ data }) => setObra(data)).catch((e) => setError(errorText(e, "Obra não encontrada")));
    }, [leadId]);
    useEffect(() => { load(); }, [load]);

    if (error) {
        return (
            <>
                <BackLink to="/parceiros" label="As minhas obras" />
                <div className={`${P_CARD} text-stone-600`} data-testid="partner-obra-error">{error}</div>
            </>
        );
    }
    if (!obra) return <PageLoading />;
    return (
        <>
            <BackLink to="/parceiros" label="As minhas obras" />
            <ObraCover key={partnerCoverUrl(obra)} src={partnerCoverUrl(obra)} className="w-full aspect-[16/7] rounded-3xl" testid="partner-obra-cover" />
            <header data-testid="partner-obra-header">
                <h1 className={`${P_H1} break-words`} data-testid="partner-obra-title">{obra.title}</h1>
                {obra.location && <p className="mt-2 flex items-center gap-1.5 text-stone-600"><MapPin className="w-4 h-4" />{obra.location}</p>}
                <span className="mt-3 inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-stone-200/70 text-stone-700" data-testid="partner-obra-status">{obra.status}</span>
            </header>
            <div className="grid gap-3">
                <Link to={`/parceiros/obras/${leadId}/nova`} className={`${P_BTN_COPPER} !py-5 text-lg`} data-testid="partner-new-update">
                    <Camera className="w-5 h-5" /> Nova atualização
                </Link>
                <Link to={`/parceiros/obras/${leadId}/questoes/nova`} className={`${P_BTN_GHOST} w-full`} data-testid="partner-new-thread">
                    <MessageSquarePlus className="w-5 h-5" /> Comunicar problema ou colocar questão
                </Link>
            </div>
            <UrgentCard compact />
            <UpdatesList leadId={leadId} updates={obra.updates} />
            <ThreadsList leadId={leadId} threads={obra.threads} />
            <WorkDays leadId={leadId} obra={obra} onChanged={load} />
        </>
    );
}

function UpdatesList({ leadId, updates }) {
    const [all, setAll] = useState(false);
    const shown = all ? updates : updates.slice(0, 5);
    return (
        <section className={P_CARD} data-testid="partner-updates-section">
            <h2 className={P_H2}>Atualizações enviadas</h2>
            {updates.length === 0 ? (
                <p className="mt-3 text-stone-500" data-testid="partner-updates-empty">Ainda não enviou atualizações desta obra.</p>
            ) : (
                <ul className="mt-4 divide-y divide-stone-100">
                    {shown.map((u) => (
                        <li key={u.id}>
                            <Link to={`/parceiros/obras/${leadId}/atualizacoes/${u.id}`} className="flex items-center gap-3 py-3.5 group" data-testid={`partner-update-${u.id}`}>
                                <span className="min-w-0 flex-1">
                                    <span className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-stone-900 capitalize">{formatDay(u.work_date)}</span>
                                        <StatusPill status={u.status} testid={`partner-update-status-${u.id}`} />
                                    </span>
                                    <span className="block text-[15px] text-stone-600 truncate mt-1">{u.works_done}</span>
                                    {u.status === "changes_requested" && u.admin_note && (
                                        <span className="block text-sm text-orange-900 mt-1 line-clamp-2">Correção: {u.admin_note}</span>
                                    )}
                                </span>
                                <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
            {updates.length > 5 && (
                <button type="button" onClick={() => setAll((v) => !v)} className="mt-2 min-h-[44px] text-[15px] font-semibold text-[hsl(var(--copper))]" data-testid="partner-updates-toggle">
                    {all ? "Mostrar menos" : `Ver todas (${updates.length})`}
                </button>
            )}
        </section>
    );
}

function ThreadsList({ leadId, threads }) {
    return (
        <section className={P_CARD} data-testid="partner-threads-section">
            <h2 className={P_H2}>Questões da obra</h2>
            <p className="text-sm text-stone-500 mt-1">Comunicação interna com O Homem da Obra. O cliente não tem acesso.</p>
            {threads.length === 0 ? (
                <p className="mt-3 text-stone-500" data-testid="partner-threads-empty">Ainda não há questões.</p>
            ) : (
                <ul className="mt-3 divide-y divide-stone-100">
                    {threads.map((t) => (
                        <li key={t.id}>
                            <Link to={`/parceiros/obras/${leadId}/questoes/${t.id}`} className="flex items-center gap-3 py-3.5" data-testid={`partner-thread-${t.id}`}>
                                <span className="min-w-0 flex-1">
                                    <span className="block font-semibold text-stone-900 break-words">{t.title}</span>
                                    <span className="block text-sm text-stone-500 mt-0.5">{formatDateTime(t.last_message_at)}</span>
                                    <span className="mt-2 flex flex-wrap gap-2">
                                        {t.unread && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[hsl(var(--copper))] text-white">Nova resposta</span>}
                                        {t.urgent && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">Urgente</span>}
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${t.status === "open" ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`}>{t.status === "open" ? "Aberta" : "Resolvida"}</span>
                                    </span>
                                </span>
                                <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function WorkDays({ leadId, obra, onChanged }) {
    const [day, setDay] = useState(obra.today);
    const [busy, setBusy] = useState(false);
    const planned = new Set(obra.planned_days);
    const days = [...new Set([...obra.planned_days, ...obra.worked_days])].sort().reverse().slice(0, 30);

    const add = async () => {
        setBusy(true);
        try {
            await partnerApi.post(`/obras/${leadId}/worked-days`, { date: day });
            toast.success("Dia registado.");
            onChanged();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className={P_CARD} data-testid="partner-days-section">
            <h2 className={P_H2}>Dias de intervenção</h2>
            {days.length === 0 ? (
                <p className="mt-3 text-stone-500">Ainda não há dias marcados.</p>
            ) : (
                <ul className="mt-4 flex flex-wrap gap-2" data-testid="partner-days-list">
                    {days.map((d) => (
                        <li key={d} className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${planned.has(d) ? "bg-[hsl(var(--forest))]/10 text-[hsl(var(--forest))]" : "bg-orange-50 text-orange-900"}`}>
                            {formatDay(d, { weekday: "short", day: "numeric", month: "short" })} · {planned.has(d) ? "Previsto" : "Trabalhado"}
                        </li>
                    ))}
                </ul>
            )}
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <input type="date" value={day} max={obra.today} onChange={(e) => setDay(e.target.value)} className={`${P_FIELD} sm:max-w-[220px]`} data-testid="partner-day-input" />
                <button type="button" onClick={add} disabled={!day || busy} className={P_BTN_GHOST} data-testid="partner-day-add">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Acrescentar dia trabalhado
                </button>
            </div>
            <p className="text-sm text-stone-500 mt-3">
                Os dias previstos são definidos por O Homem da Obra. Os dias que acrescentar servem apenas de registo e não representam autorização de trabalhos extra nem alterações comerciais.
            </p>
        </section>
    );
}
